import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetHeader,
  BottomSheetFooter,
  BottomSheetTitle,
  BottomSheetDescription,
} from '@/components/ui/bottom-sheet';
import { formatDate, cn } from '@/lib/utils';
import { Plus, Trash2, Key, Copy, Check, Globe, ChevronRight, RefreshCw } from 'lucide-react';

export function Settings() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteSheet, setDeleteSheet] = useState<any>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyResult, setNewKeyResult] = useState<{ key: string; id: string } | null>(null);
  const [copied, setCopied] = useState(false);
  
  const queryClient = useQueryClient();

  const { data: apiKeys, isLoading, refetch } = useQuery({
    queryKey: ['api-keys'],
    queryFn: adminApi.getApiKeys,
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => adminApi.createApiKey(name),
    onSuccess: (data) => {
      setNewKeyResult(data);
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setDeleteSheet(null);
    },
  });

  const handleCreateKey = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(newKeyName);
  };

  const handleCopyKey = () => {
    if (newKeyResult?.key) {
      navigator.clipboard.writeText(newKeyResult.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setNewKeyName('');
    setNewKeyResult(null);
    setCopied(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage your configuration</p>
        </div>
        <button 
          onClick={() => refetch()} 
          className="p-3 rounded-xl bg-secondary/50 active:scale-95 active:bg-secondary transition-all"
        >
          <RefreshCw className={cn("h-5 w-5 text-muted-foreground", isLoading && "animate-spin")} />
        </button>
      </div>

      {/* API Keys Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-muted/50 flex items-center justify-center">
              <Key className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <h2 className="font-medium">API Keys</h2>
              <p className="text-xs text-muted-foreground">Manage admin access</p>
            </div>
          </div>
          <Button size="default" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Key
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
          </div>
        ) : apiKeys?.length === 0 ? (
          <div className="mobile-card text-center py-8">
            <Key className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No API keys created yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {apiKeys?.map((key: any) => (
              <div key={key.id} className="mobile-card flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center shrink-0">
                  <Key className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{key.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {key.lastUsedAt ? `Used ${formatDate(key.lastUsedAt)}` : 'Never used'}
                  </p>
                </div>
                <button 
                  onClick={() => setDeleteSheet(key)}
                  className="p-2 rounded-lg text-muted-foreground hover:text-status-error-foreground hover:bg-status-error/20 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Configuration Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-muted/50 flex items-center justify-center">
            <Globe className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <h2 className="font-medium">Configuration</h2>
            <p className="text-xs text-muted-foreground">Current API settings</p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="mobile-card">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">API URL</p>
            <p className="font-mono text-sm break-all">
              {import.meta.env.VITE_API_URL || 'http://localhost:3000'}
            </p>
          </div>
          <div className="mobile-card">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Current API Key</p>
            <p className="font-mono text-sm">
              {localStorage.getItem('admin_api_key')?.slice(0, 20)}...
            </p>
          </div>
        </div>
      </div>

      {/* Create API Key Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for admin access
            </DialogDescription>
          </DialogHeader>
          
          {newKeyResult ? (
            <div className="space-y-4">
              <div className="rounded-xl bg-status-warning/20 border border-status-warning p-4">
                <p className="text-sm text-status-warning-foreground mb-3">
                  ⚠️ Save this key now — it won't be shown again!
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm bg-background p-3 rounded-lg border border-border font-mono break-all">
                    {newKeyResult.key}
                  </code>
                  <Button variant="outline" size="icon" onClick={handleCopyKey}>
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button size="lg" onClick={handleCloseDialog} className="w-full">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleCreateKey} className="space-y-4">
              <div>
                <label className="text-sm font-medium">Key Name</label>
                <Input
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="My Admin Key"
                  required
                  className="mt-1.5 h-12"
                />
              </div>
              <DialogFooter className="pt-4">
                <Button type="button" variant="secondary" size="lg" onClick={handleCloseDialog}>
                  Cancel
                </Button>
                <Button type="submit" size="lg" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Bottom Sheet */}
      <BottomSheet open={!!deleteSheet} onOpenChange={() => setDeleteSheet(null)}>
        <BottomSheetContent>
          <BottomSheetHeader>
            <BottomSheetTitle>Delete API Key</BottomSheetTitle>
            <BottomSheetDescription>
              Are you sure you want to delete "{deleteSheet?.name}"? This action cannot be undone.
            </BottomSheetDescription>
          </BottomSheetHeader>
          <BottomSheetFooter>
            <Button
              size="xl"
              variant="destructive"
              onClick={() => deleteSheet && deleteMutation.mutate(deleteSheet.id)}
              disabled={deleteMutation.isPending}
              className="w-full"
            >
              {deleteMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Key
                </>
              )}
            </Button>
            <Button
              size="xl"
              variant="secondary"
              onClick={() => setDeleteSheet(null)}
              className="w-full"
            >
              Cancel
            </Button>
          </BottomSheetFooter>
        </BottomSheetContent>
      </BottomSheet>
    </div>
  );
}
